import { Component, EventEmitter, Output } from "@angular/core";
import { PublishStateService } from "app/js/src/infrastructure/clipping-studio/publish-state.service";

export type RightSidebarTab = "dest" | "metadata" | "thumbnail" | "publish";

const WIZARD_STEPS: readonly RightSidebarTab[] = [
  "dest",
  "thumbnail",
  "metadata",
  "publish",
];

@Component({
  selector: "evrideo-clipping-studio-right-sidebar",
  templateUrl: "./clipping-studio-right-sidebar.component.html",
  styleUrls: ["./clipping-studio-right-sidebar.component.scss"],
})
export class ClippingStudioRightSidebarComponent {
  @Output() close = new EventEmitter<void>();
  @Output() publishContinue = new EventEmitter<void>();
  @Output() publishExit = new EventEmitter<void>();

  activeTab: RightSidebarTab = "dest";
  private isAdvancingStep = false;

  constructor(public publishState: PublishStateService) {}

  get publishDisabledTitle(): string {
    return this.publishState.publishDisabledReason() ?? "";
  }

  get destNextDisabledTitle(): string {
    return this.publishState.destStepDisabledReason() ?? "";
  }

  get isDestNextDisabled(): boolean {
    return !this.publishState.canLeaveDestStep() || this.isAdvancingStep;
  }

  get metaNextDisabledTitle(): string {
    return this.publishState.metaStepDisabledReason() ?? "";
  }

  get isMetaNextDisabled(): boolean {
    return !this.publishState.canLeaveMetaStep() || this.isAdvancingStep;
  }

  get isThumbNextDisabled(): boolean {
    return this.isAdvancingStep;
  }

  /** Step-specific gate message, or publish pre-submit error on Publish. */
  get footerStatusMessage(): string | null {
    if (this.activeTab === "dest") {
      return this.publishState.destStepDisabledReason();
    }
    if (this.activeTab === "metadata") {
      return this.publishState.metadataStepBlockers()[0]?.message ?? null;
    }
    if (this.activeTab === "publish") {
      return this.publishState.preSubmitError();
    }
    return null;
  }

  get isFirstStep(): boolean {
    return this.activeTab === WIZARD_STEPS[0];
  }

  get isLastStep(): boolean {
    return this.activeTab === WIZARD_STEPS[WIZARD_STEPS.length - 1];
  }

  get showNavRow(): boolean {
    return !this.isFirstStep;
  }

  isStepActive(step: RightSidebarTab): boolean {
    return this.activeTab === step;
  }

  /** Steps before the current one show a checkmark instead of the step number. */
  isStepCompleted(step: RightSidebarTab): boolean {
    const current = WIZARD_STEPS.indexOf(this.activeTab);
    const target = WIZARD_STEPS.indexOf(step);
    return current > 0 && target >= 0 && target < current;
  }

  onClose(): void {
    this.close.emit();
  }

  async goNext(): Promise<void> {
    const index = WIZARD_STEPS.indexOf(this.activeTab);
    if (index < 0 || index >= WIZARD_STEPS.length - 1 || this.isAdvancingStep) {
      return;
    }

    if (this.activeTab === "dest") {
      if (!this.publishState.canLeaveDestStep()) {
        return;
      }
      this.isAdvancingStep = true;
      try {
        await this.publishState.saveProjectNow();
      } finally {
        this.isAdvancingStep = false;
      }
      if (!this.publishState.canLeaveDestStep()) {
        return;
      }
    } else if (this.activeTab === "thumbnail") {
      this.isAdvancingStep = true;
      try {
        await this.publishState.saveProjectNow();
      } finally {
        this.isAdvancingStep = false;
      }
    } else if (this.activeTab === "metadata") {
      if (!this.publishState.canLeaveMetaStep()) {
        return;
      }
      this.isAdvancingStep = true;
      try {
        await this.publishState.saveProjectNow();
      } finally {
        this.isAdvancingStep = false;
      }
      if (!this.publishState.canLeaveMetaStep()) {
        return;
      }
    }

    this.activeTab = WIZARD_STEPS[index + 1];
  }

  goBack(): void {
    const index = WIZARD_STEPS.indexOf(this.activeTab);
    if (index <= 0) {
      return;
    }
    this.activeTab = WIZARD_STEPS[index - 1];
  }
}
